import React from 'react';
import { ReturnedPortfolio, Language } from '../Create-Portfolio/types';
import { portfolioFileLabel } from '../../utils/portfolioFileLabel';
import { ReactComponent as FolderIcon } from '../../images/folder-fill.svg';
import { ReactComponent as TrophyIcon } from '../../images/trophy-fill.svg';
import { ReactComponent as AwardsIcon } from '../../images/award-fill.svg';
import { ReactComponent as BuidingIcon } from '../../images/building.svg';
import { ReactComponent as LinkIcon } from '../../images/link-45deg.svg';
import { normalizeUrl } from '../Preview/helpers';
import {
  isReferenceVerified,
  normalizePublicPortfolio,
  parseReferenceVerificationAnswers,
  type ReferenceVerificationAnswers,
  verifiedReferencesOnly,
} from '../../utils/publicProfileDisplay';

interface PortfolioTabProps {
  portfolio: ReturnedPortfolio;
  setPortfolioData?: React.Dispatch<React.SetStateAction<ReturnedPortfolio>>;
  setSavePortfolioButton?: React.Dispatch<React.SetStateAction<boolean>>;
  savePortfolioButton?: boolean;
  deletePortfolioContext?: boolean;
  getData?: () => Promise<void>;
  onSectionExpand?: (sectionKey: string, sectionTitle: string) => void;
  onDocumentOpen?: (documentKey: string, documentName: string) => void;
  viewerType?: 'recruiter' | 'candidate_self' | 'anonymous';
  candidateId?: number;
  shareToken?: string | null;
  hasFullAccess?: boolean;
}

const PORTFOLIO_SECTION_KEYS = [
  'workCulture',
  'languages',
  'projects',
  'accomplishments',
  'certifications',
  'organizations',
  'references',
] as const;

const DEFAULT_COLLAPSED: Record<string, boolean> = Object.fromEntries(
  PORTFOLIO_SECTION_KEYS.map((key) => [key, true])
);

const LANGUAGE_LEVEL_COLOR: Record<string, string> = {
  Native: '#00b14f',
  Fluent: '#0077b6',
  Advanced: '#6f42c1',
  Intermediate: '#fd7e14',
  Beginner: '#6c757d',
};

const CHIP_COLORS = ['#e8f5e9', '#e3f2fd', '#f3e5f5', '#fff3e0', '#e0f7fa', '#fce4ec', '#f1f8e9'];
const CHIP_TEXT_COLORS = [
  '#2e7d32',
  '#1565c0',
  '#6a1b9a',
  '#e65100',
  '#006064',
  '#880e4f',
  '#33691e',
];

const fmt = (d?: string | null) => {
  if (!d) return '';
  const dt = new Date(d);
  return dt.toLocaleDateString('en-GB', { day: '2-digit', month: '2-digit', year: 'numeric' });
};

const ReferenceFeedbackDetails: React.FC<{
  answers: ReferenceVerificationAnswers | null;
}> = ({ answers }) => {
  if (!answers) return null;

  const blocks = [
    {
      key: 'together',
      tone: 'together',
      label: 'How they worked together',
      text: answers.relationship_summary?.trim(),
      icon: (
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" aria-hidden>
          <path
            d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
          />
          <circle cx="9" cy="7" r="4" stroke="currentColor" strokeWidth="2" />
          <path
            d="M23 21v-2a4 4 0 0 0-3-3.87M16 3.13a4 4 0 0 1 0 7.75"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
          />
        </svg>
      ),
    },
    {
      key: 'strengths',
      tone: 'strengths',
      label: 'Strengths highlighted',
      text: answers.strengths?.trim(),
      icon: (
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" aria-hidden>
          <path
            d="M12 2l2.4 7.2H22l-6 4.6 2.3 7.2L12 17.8 5.7 21l2.3-7.2-6-4.6h7.6L12 2z"
            stroke="currentColor"
            strokeWidth="1.8"
            strokeLinejoin="round"
          />
        </svg>
      ),
    },
    {
      key: 'concerns',
      tone: 'concerns',
      label: 'Concerns or context',
      text: answers.concerns?.trim(),
      icon: (
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" aria-hidden>
          <path
            d="M21 15a4 4 0 0 1-4 4H8l-5 3 1.2-4.8A4 4 0 0 1 4 15V7a4 4 0 0 1 4-4h9a4 4 0 0 1 4 4v8z"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinejoin="round"
          />
        </svg>
      ),
    },
  ].filter((block) => Boolean(block.text));

  if (!blocks.length) return null;

  return (
    <div className="pub-ref-feedback">
      {blocks.map((block) => (
        <div key={block.key} className={`pub-ref-feedback-row pub-ref-feedback-row--${block.tone}`}>
          <div className="pub-ref-feedback-icon">{block.icon}</div>
          <div className="pub-ref-feedback-copy">
            <div className="pub-ref-feedback-label">{block.label}</div>
            <p>{block.text}</p>
          </div>
        </div>
      ))}
    </div>
  );
};

const ReferenceCard: React.FC<{
  reference: Record<string, unknown>;
}> = ({ reference }) => {
  const answers = parseReferenceVerificationAnswers(
    reference.verificationAnswers ?? reference.verification_answers
  );
  const verified = isReferenceVerified(reference);
  const name = String(reference.name ?? '').trim() || 'Reference';
  const organization = String(reference.organization ?? reference.company ?? '').trim();
  const role = String(reference.role ?? '').trim();
  const email = String(reference.email ?? '').trim();
  const verifiedAt = String(reference.verified_at ?? reference.verifiedAt ?? '').trim();
  const hasFeedback = Boolean(
    answers &&
      (answers.relationship_summary?.trim() ||
        answers.strengths?.trim() ||
        answers.concerns?.trim())
  );

  return (
    <article className="pub-ref-card">
      <div className="pub-ref-card-top">
        <div className="pub-ref-card-identity">
          <div className="pub-ref-avatar">{name.charAt(0).toUpperCase()}</div>
          <div className="pub-ref-card-intro">
            <h4 className="pub-ref-name">{name}</h4>
            <p className="pub-ref-org">{[role, organization].filter(Boolean).join(' · ')}</p>
            {email ? <p className="pub-ref-email">{email}</p> : null}
            {answers &&
            (answers.worked_together !== undefined || answers.recommend_hire !== undefined) ? (
              <div className="pub-ref-meta-pills">
                {answers.worked_together !== undefined && (
                  <span className="pub-ref-meta-pill">
                    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" aria-hidden>
                      <path
                        d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"
                        stroke="currentColor"
                        strokeWidth="2"
                      />
                      <circle cx="9" cy="7" r="4" stroke="currentColor" strokeWidth="2" />
                    </svg>
                    Worked together: <strong>{answers.worked_together ? 'Yes' : 'No'}</strong>
                  </span>
                )}
                {answers.recommend_hire !== undefined && (
                  <span
                    className={`pub-ref-meta-pill${
                      answers.recommend_hire ? ' pub-ref-meta-pill--positive' : ''
                    }`}
                  >
                    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" aria-hidden>
                      <path
                        d="M12 2l2.4 7.2H22l-6 4.6 2.3 7.2L12 17.8 5.7 21l2.3-7.2-6-4.6h7.6L12 2z"
                        stroke="currentColor"
                        strokeWidth="1.8"
                      />
                    </svg>
                    Would recommend: <strong>{answers.recommend_hire ? 'Yes' : 'No'}</strong>
                  </span>
                )}
              </div>
            ) : null}
          </div>
        </div>
        {verified ? (
          <div className="pub-ref-verify-col">
            <span className="pub-ref-verify-badge">
              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" aria-hidden>
                <path
                  d="M20 6L9 17l-5-5"
                  stroke="currentColor"
                  strokeWidth="2.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
              Verified
            </span>
            {verifiedAt ? (
              <span className="pub-ref-verify-date">Verified on {fmt(verifiedAt)}</span>
            ) : null}
          </div>
        ) : null}
      </div>

      {verified && hasFeedback ? <ReferenceFeedbackDetails answers={answers} /> : null}

      {verified ? (
        <div className="pub-ref-status">
          <div className="pub-ref-status-icon" aria-hidden>
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
              <path
                d="M12 3l7 3v5c0 5-3.2 8.4-7 10-3.8-1.6-7-5-7-10V6l7-3z"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinejoin="round"
              />
              <path
                d="M9.5 12.2l1.8 1.8 3.4-3.6"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </div>
          <div className="pub-ref-status-copy">
            <strong>Reference verified</strong>
            <p>This reference has been verified and is active.</p>
          </div>
        </div>
      ) : null}
    </article>
  );
};

const IcAsk = () => (
  <svg
    width="14"
    height="14"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <circle cx="12" cy="12" r="10" />
    <path d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3" />
    <line x1="12" y1="17" x2="12.01" y2="17" />
  </svg>
);

/* ─── Sub-components ─────────────────────────────────────────────────────── */

const SectionHeader: React.FC<{
  title: string;
  subtitle: string;
  count?: number;
  collapsed: boolean;
  onToggle: () => void;
  icon: React.ReactNode;
  onAsk?: () => void;
  onLike?: () => void;
  liked?: boolean;
}> = ({ title, subtitle, count, collapsed, onToggle, icon, onAsk, onLike, liked = false }) => (
  <div
    className="pub-section-header pub-section-header--clickable"
    onClick={onToggle}
    onKeyDown={(event) => {
      if (event.key === 'Enter' || event.key === ' ') {
        event.preventDefault();
        onToggle();
      }
    }}
    role="button"
    tabIndex={0}
    aria-expanded={!collapsed}
  >
    <div className="pub-section-header-left">
      <span className="pub-section-icon">{icon}</span>
      <div>
        <div className="pub-section-title">
          {title}
          {count !== undefined && count > 0 && <span className="pub-section-count">{count}</span>}
        </div>
        <div className="pub-section-subtitle">{subtitle}</div>
      </div>
    </div>
    <div
      className="pub-section-header-actions"
      onClick={(event) => event.stopPropagation()}
      onKeyDown={(event) => event.stopPropagation()}
    >
      {onAsk ? (
        <button
          type="button"
          className="candidate-info-ask-button"
          onClick={onAsk}
          title={`Ask about ${title}`}
        >
          <IcAsk />
        </button>
      ) : null}
      {onLike ? (
        <button
          type="button"
          className={`candidate-info-like-button${liked ? ' liked' : ''}`}
          onClick={onLike}
          title={liked ? 'Unlike' : 'Like this section'}
        >
          {liked ? '♥' : '♡'}
        </button>
      ) : null}
      <button
        type="button"
        className="pub-collapse-btn"
        onClick={onToggle}
        title={collapsed ? 'Expand' : 'Collapse'}
        aria-hidden="true"
        tabIndex={-1}
      >
        <svg
          width="14"
          height="14"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2.5"
          strokeLinecap="round"
          strokeLinejoin="round"
          style={{
            transform: collapsed ? 'rotate(-90deg)' : 'rotate(0deg)',
            transition: 'transform 0.25s ease',
          }}
        >
          <polyline points="6 9 12 15 18 9" />
        </svg>
      </button>
    </div>
  </div>
);

const ItemLinks: React.FC<{
  filesUrl?: string[];
  links?: string[];
  link?: string;
  onDocumentOpen?: (documentKey: string, documentName: string) => void;
}> = ({ filesUrl, links, link, onDocumentOpen }) => {
  const all: { href: string; label: string }[] = [];

  filesUrl?.forEach((f) => {
    const href = normalizeUrl(f);
    all.push({ href, label: portfolioFileLabel(f) });
  });

  links?.forEach((l) => all.push({ href: normalizeUrl(l), label: 'View Link' }));
  if (link) all.push({ href: normalizeUrl(link), label: 'View Link' });

  if (!all.length) return null;
  return (
    <div className="pub-links">
      {all.map((a, i) => (
        <a
          key={i}
          href={a.href}
          target="_blank"
          rel="noopener noreferrer"
          className="pub-link"
          onClick={() =>
            onDocumentOpen?.(`portfolio-doc-${i + 1}`, a.label || 'Portfolio document')
          }
        >
          <LinkIcon className="pub-link-icon" />
          {a.label}
        </a>
      ))}
    </div>
  );
};

const ItemCard: React.FC<{
  icon: React.ReactNode;
  title: string;
  meta?: string;
  description?: string;
  filesUrl?: string[];
  links?: string[];
  link?: string;
  onDocumentOpen?: (documentKey: string, documentName: string) => void;
}> = ({ icon, title, meta, description, filesUrl, links, link, onDocumentOpen }) => {
  const hasBody = description || (filesUrl && filesUrl.length) || (links && links.length) || link;
  return (
    <div className="pub-item-card">
      <div className="pub-item-header">
        <span className="pub-item-icon">{icon}</span>
        <div className="pub-item-title-wrap">
          <span className="pub-item-title">{title || '(Untitled)'}</span>
          {meta && <span className="pub-item-meta">{meta}</span>}
        </div>
      </div>
      {hasBody && (
        <>
          <div className="pub-item-divider" />
          {description && <p className="pub-item-description">{description}</p>}
          <ItemLinks
            filesUrl={filesUrl}
            links={links}
            link={link}
            onDocumentOpen={onDocumentOpen}
          />
        </>
      )}
    </div>
  );
};

/* ─── Main Component ─────────────────────────────────────────────────────── */

const PublicUserProfileTabContent: React.FC<PortfolioTabProps> = ({
  portfolio,
  onSectionExpand,
  onDocumentOpen,
  viewerType = 'anonymous',
  hasFullAccess = false,
}) => {
  const [collapsed, setCollapsed] = React.useState<Record<string, boolean>>(DEFAULT_COLLAPSED);

  // Candidate-app public/preview view matches recruiter UI but is read-only.
  const sectionActions = (_sectionKey: string, _title: string) => ({});

  const safePortfolio = React.useMemo(() => {
    const normalized = normalizePublicPortfolio(portfolio);
    const references =
      hasFullAccess && viewerType !== 'candidate_self'
        ? verifiedReferencesOnly(normalized.references as unknown[])
        : normalized.references;
    return { ...normalized, references };
  }, [portfolio, hasFullAccess, viewerType]);

  const toggle = (key: string, title: string) => {
    setCollapsed((prev) => {
      const willExpand = Boolean(prev[key]);
      if (willExpand) {
        onSectionExpand?.(key, title);
      }
      return { ...prev, [key]: !prev[key] };
    });
  };

  const hasAnyContent =
    Boolean(safePortfolio.overview?.trim()) ||
    safePortfolio.projects.length > 0 ||
    safePortfolio.accomplishments.length > 0 ||
    safePortfolio.certifications.length > 0 ||
    safePortfolio.organizations.length > 0 ||
    safePortfolio.languages.length > 0 ||
    safePortfolio.workCulture.length > 0 ||
    safePortfolio.references.length > 0 ||
    safePortfolio.isLanguagesEnabled !== false ||
    safePortfolio.isWorkCultureEnabled !== false ||
    safePortfolio.isReferencesEnabled !== false;

  if (!hasAnyContent) {
    return (
      <div className="pub-portfolio-empty">
        <svg
          width="40"
          height="40"
          viewBox="0 0 24 24"
          fill="none"
          stroke="#ced4da"
          strokeWidth="1.5"
          strokeLinecap="round"
          strokeLinejoin="round"
        >
          <path d="M2 17L12 22L22 17" />
          <path d="M2 12L12 17L22 12" />
          <path d="M12 2L2 7L12 12L22 7L12 2Z" />
        </svg>
        <p>This candidate has not published their portfolio yet.</p>
      </div>
    );
  }

  return (
    <div className="pub-portfolio-wrap">
      {/* ── Overview ───────────────────────────────────── */}
      {safePortfolio.overview?.trim() && (
        <div className="pub-overview-card">
          <div className="pub-overview-accent" />
          <div className="pub-overview-body">
            <div className="pub-overview-label">Overview</div>
            <p className="pub-overview-text">{safePortfolio.overview}</p>
          </div>
        </div>
      )}

      {/* ── Work Culture ────────────────────────────────── */}
      {safePortfolio.isWorkCultureEnabled !== false && (
        <div className="pub-portfolio-section">
          <SectionHeader
            title="Work Culture"
            subtitle="What kind of environment fits this candidate"
            count={safePortfolio.workCulture.length || undefined}
            collapsed={!!collapsed['workCulture']}
            onToggle={() => toggle('workCulture', 'Work Culture')}
            {...sectionActions('workCulture', 'Work Culture')}
            icon={
              <svg
                width="16"
                height="16"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <circle cx="12" cy="12" r="10" />
                <path d="M8 14s1.5 2 4 2 4-2 4-2" />
                <line x1="9" y1="9" x2="9.01" y2="9" />
                <line x1="15" y1="9" x2="15.01" y2="9" />
              </svg>
            }
          />
          {!collapsed['workCulture'] && (
            <div className="pub-section-body">
              {safePortfolio.workCulture.length > 0 ? (
                <div className="pub-culture-chips">
                  {safePortfolio.workCulture.map((item, i) => (
                    <span
                      key={i}
                      className="pub-culture-chip"
                      style={{
                        backgroundColor: CHIP_COLORS[i % CHIP_COLORS.length],
                        color: CHIP_TEXT_COLORS[i % CHIP_TEXT_COLORS.length],
                      }}
                    >
                      {item}
                    </span>
                  ))}
                </div>
              ) : (
                <p className="pub-portfolio-empty-inline">
                  No work culture preferences listed yet.
                </p>
              )}
            </div>
          )}
        </div>
      )}

      {/* ── Languages ───────────────────────────────────── */}
      {safePortfolio.isLanguagesEnabled !== false && (
        <div className="pub-portfolio-section">
          <SectionHeader
            title="Languages"
            subtitle="Languages this candidate can communicate in"
            count={safePortfolio.languages.length || undefined}
            collapsed={!!collapsed['languages']}
            onToggle={() => toggle('languages', 'Languages')}
            {...sectionActions('languages', 'Languages')}
            icon={
              <svg
                width="16"
                height="16"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <path d="M5 8l6 6" />
                <path d="M4 14l6-6 2-3" />
                <path d="M2 5h12" />
                <path d="M7 2h1" />
                <path d="M22 22l-5-10-5 10" />
                <path d="M14 18h6" />
              </svg>
            }
          />
          {!collapsed['languages'] && (
            <div className="pub-section-body">
              {safePortfolio.languages.length > 0 ? (
                <div className="pub-language-list">
                  {safePortfolio.languages.map((lang: Language, i: number) => (
                    <div key={i} className="pub-language-row">
                      <span className="pub-language-name">{lang.language}</span>
                      <span
                        className="pub-language-level"
                        style={{
                          backgroundColor: LANGUAGE_LEVEL_COLOR[lang.level] || '#6c757d',
                        }}
                      >
                        {lang.level}
                      </span>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="pub-portfolio-empty-inline">No languages listed yet.</p>
              )}
            </div>
          )}
        </div>
      )}

      {/* ── Projects ─────────────────────────────────────── */}
      {safePortfolio.projects.length > 0 && (
        <div className="pub-portfolio-section">
          <SectionHeader
            title="Projects"
            subtitle="Major work experiences and collaborations"
            count={safePortfolio.projects.length}
            collapsed={!!collapsed['projects']}
            onToggle={() => toggle('projects', 'Projects')}
            {...sectionActions('projects', 'Projects')}
            icon={<FolderIcon className="pub-svg-icon projects" />}
          />
          {!collapsed['projects'] && (
            <div className="pub-section-body">
              <div className="pub-item-list pub-item-list--cards">
                {safePortfolio.projects.map((p: any, i: number) => (
                  <ItemCard
                    key={i}
                    icon={<FolderIcon className="pub-svg-icon projects" />}
                    title={p.name}
                    description={p.description}
                    filesUrl={p.filesUrl}
                    links={p.links}
                    onDocumentOpen={onDocumentOpen}
                  />
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {/* ── Accomplishments ───────────────────────────────── */}
      {safePortfolio.accomplishments.length > 0 && (
        <div className="pub-portfolio-section">
          <SectionHeader
            title="Accomplishments"
            subtitle="Notable achievements and milestones"
            count={safePortfolio.accomplishments.length}
            collapsed={!!collapsed['accomplishments']}
            onToggle={() => toggle('accomplishments', 'Accomplishments')}
            {...sectionActions('accomplishments', 'Accomplishments')}
            icon={<TrophyIcon className="pub-svg-icon accomplishments" />}
          />
          {!collapsed['accomplishments'] && (
            <div className="pub-section-body">
              <div className="pub-item-list pub-item-list--cards">
                {safePortfolio.accomplishments.map((a: any, i: number) => (
                  <ItemCard
                    key={i}
                    icon={<TrophyIcon className="pub-svg-icon accomplishments" />}
                    title={a.name}
                    meta={a.date ? fmt(a.date) : undefined}
                    description={a.description}
                    filesUrl={a.filesUrl}
                    links={a.links}
                    onDocumentOpen={onDocumentOpen}
                  />
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {/* ── Certifications ────────────────────────────────── */}
      {safePortfolio.certifications.length > 0 && (
        <div className="pub-portfolio-section">
          <SectionHeader
            title="Certifications"
            subtitle="Credentials that stand out in their career"
            count={safePortfolio.certifications.length}
            collapsed={!!collapsed['certifications']}
            onToggle={() => toggle('certifications', 'Certifications')}
            {...sectionActions('certifications', 'Certifications')}
            icon={<AwardsIcon className="pub-svg-icon certifications" />}
          />
          {!collapsed['certifications'] && (
            <div className="pub-section-body">
              <div className="pub-item-list pub-item-list--cards">
                {safePortfolio.certifications.map((c: any, i: number) => (
                  <ItemCard
                    key={i}
                    icon={<AwardsIcon className="pub-svg-icon certifications" />}
                    title={c.name}
                    meta={c.date ? fmt(c.date) : undefined}
                    filesUrl={
                      Array.isArray(c.filesUrl)
                        ? c.filesUrl.map((f: any) => (typeof f === 'string' ? f : f.name))
                        : []
                    }
                    onDocumentOpen={onDocumentOpen}
                  />
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {/* ── Organizations ─────────────────────────────────── */}
      {safePortfolio.organizations.length > 0 && (
        <div className="pub-portfolio-section">
          <SectionHeader
            title="Organizations"
            subtitle="Memberships, volunteer work and leadership roles"
            count={safePortfolio.organizations.length}
            collapsed={!!collapsed['organizations']}
            onToggle={() => toggle('organizations', 'Organizations')}
            {...sectionActions('organizations', 'Organizations')}
            icon={<BuidingIcon className="pub-svg-icon organizations" />}
          />
          {!collapsed['organizations'] && (
            <div className="pub-section-body">
              <div className="pub-item-list pub-item-list--cards">
                {safePortfolio.organizations.map((o: any, i: number) => {
                  const meta =
                    o.startDate || o.endDate
                      ? (o.startDate ? fmt(o.startDate) : '—') +
                        ' – ' +
                        (o.endDate ? fmt(o.endDate) : 'Present')
                      : undefined;
                  return (
                    <ItemCard
                      key={i}
                      icon={<BuidingIcon className="pub-svg-icon organizations" />}
                      title={o.name}
                      meta={meta}
                      description={o.description}
                      link={o.link}
                      onDocumentOpen={onDocumentOpen}
                    />
                  );
                })}
              </div>
            </div>
          )}
        </div>
      )}

      {/* ── References ────────────────────────────────────── */}
      {safePortfolio.isReferencesEnabled !== false && (
        <div className="pub-portfolio-section">
          <SectionHeader
            title="References"
            subtitle="Professional contacts who can vouch for this candidate"
            count={safePortfolio.references.length || undefined}
            collapsed={!!collapsed['references']}
            onToggle={() => toggle('references', 'References')}
            {...sectionActions('references', 'References')}
            icon={
              <svg
                width="16"
                height="16"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
                <circle cx="9" cy="7" r="4" />
                <path d="M23 21v-2a4 4 0 0 0-3-3.87" />
                <path d="M16 3.13a4 4 0 0 1 0 7.75" />
              </svg>
            }
          />
          {!collapsed['references'] && (
            <div className="pub-section-body">
              {safePortfolio.references.length > 0 ? (
                <div className="pub-ref-panel">
                  {safePortfolio.references.map((r: any, i: number) => (
                    <ReferenceCard key={i} reference={r} />
                  ))}
                </div>
              ) : (
                <p className="pub-portfolio-empty-inline">
                  {hasFullAccess && viewerType !== 'candidate_self'
                    ? 'No verified references yet.'
                    : hasFullAccess
                      ? 'No references listed yet.'
                      : 'References are available to recruiters with full access.'}
                </p>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default PublicUserProfileTabContent;
